import React, { useEffect, useState } from "react";
import api, { fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, Link2, Download, Copy, Check } from "lucide-react";

const SECTIONS = [
  {
    id: "general",
    label: "General",
    fields: [
      { key: "about", label: "Short tagline (used in footers)", type: "textarea" },
    ],
  },
  {
    id: "contact",
    label: "Contact details",
    fields: [
      { key: "contact_email", label: "Contact email" },
      { key: "contact_phone", label: "Contact phone" },
      { key: "address", label: "Address" },
      { key: "advertise_email", label: "Advertise email" },
    ],
  },
  {
    id: "socials",
    label: "Social links",
    fields: [
      { key: "instagram", label: "Instagram URL" },
      { key: "twitter", label: "Twitter / X URL" },
      { key: "facebook", label: "Facebook URL" },
      { key: "youtube", label: "YouTube URL" },
    ],
  },
  {
    id: "about_page",
    label: "About Us page",
    fields: [
      { key: "about_title", label: "Page title (large heading)" },
      { key: "about_body", label: "Page body", type: "longtext" },
      { key: "about_image_url", label: "Hero image", type: "image" },
    ],
  },
  {
    id: "advertise_page",
    label: "Advertise page",
    fields: [
      { key: "advertise_title", label: "Page title" },
      { key: "advertise_body", label: "Page body", type: "longtext" },
      { key: "advertise_image_url", label: "Hero image", type: "image" },
    ],
  },
  {
    id: "contact_page",
    label: "Contact page",
    fields: [
      { key: "contact_title", label: "Page title" },
      { key: "contact_body", label: "Page body", type: "longtext" },
      { key: "contact_image_url", label: "Hero image", type: "image" },
    ],
  },
  {
    id: "brand",
    label: "Brand assets",
    type: "brand",
  },
];

const ALL_KEYS = SECTIONS.flatMap((s) => (s.fields || []).map((f) => f.key));
const EMPTY = ALL_KEYS.reduce((a, k) => ({ ...a, [k]: "" }), {});

export default function SettingsForm() {
  const [form, setForm] = useState(EMPTY);
  const [active, setActive] = useState("general");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setForm({ ...EMPTY, ...data })).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const activeSection = SECTIONS.find((s) => s.id === active);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10" data-testid="settings-form">
      {/* Section nav */}
      <aside className="space-y-1">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={`settings-section-${s.id}`}
            onClick={() => setActive(s.id)}
            className={`w-full text-left px-4 py-2.5 rounded-md text-sm transition-colors ${
              active === s.id
                ? "bg-[#1A1A1A] text-white"
                : "text-[#76746F] hover:bg-black/5 hover:text-[#1A1A1A]"
            }`}
          >
            {s.label}
          </button>
        ))}
      </aside>

      {/* Active section content */}
      <form onSubmit={submit} className="max-w-2xl space-y-7">
        <header>
          <p className="overline text-[#76746F]">Configure</p>
          <h2 className="font-serif-display text-3xl tracking-tight mt-1">{activeSection.label}</h2>
        </header>

        {activeSection.type === "brand" ? (
          <BrandAssets />
        ) : (
          activeSection.fields.map((f) => (
            <FieldRow key={f.key} field={f} value={form[f.key] || ""} onChange={(v) => update(f.key, v)} />
          ))
        )}

        {activeSection.type !== "brand" && (
          <div className="pt-2">
            <button
              data-testid="save-settings"
              type="submit"
              disabled={busy}
              className="bg-[#1A1A1A] text-white px-7 py-3 rounded-full hover:bg-[#D4A373] transition-colors text-sm disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  const [mode, setMode] = useState(value && value.startsWith("/api/") ? "upload" : "url");
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (field.type === "textarea") {
    return (
      <FieldShell label={field.label}>
        <textarea
          data-testid={`settings-${field.key}`}
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-black/15 rounded-lg p-4 outline-none focus:border-black font-sans-modern text-sm leading-relaxed resize-none"
        />
      </FieldShell>
    );
  }

  if (field.type === "longtext") {
    return (
      <FieldShell label={field.label} hint="Use a blank line to start a new paragraph.">
        <textarea
          data-testid={`settings-${field.key}`}
          rows={9}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-white border border-black/15 rounded-lg p-4 outline-none focus:border-black font-sans-modern text-sm leading-relaxed resize-y"
        />
      </FieldShell>
    );
  }

  if (field.type === "image") {
    return (
      <FieldShell label={field.label}>
        <div className="flex gap-2 mb-3">
          <ModeButton active={mode === "upload"} onClick={() => setMode("upload")} icon={<Upload size={13} />}>Upload</ModeButton>
          <ModeButton active={mode === "url"} onClick={() => setMode("url")} icon={<Link2 size={13} />}>From URL</ModeButton>
        </div>
        {mode === "upload" ? (
          <label data-testid={`settings-${field.key}-upload`} className="flex flex-col items-center justify-center border-2 border-dashed border-black/20 rounded-xl py-8 cursor-pointer hover:border-black hover:bg-black/5 transition-colors">
            <Upload size={18} className="text-[#76746F] mb-2" strokeWidth={1.5} />
            <span className="text-sm text-[#76746F]">
              {uploading ? "Uploading…" : "Click to upload (jpg, png, webp)"}
            </span>
            <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          </label>
        ) : (
          <input
            data-testid={`settings-${field.key}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://…"
            className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern"
          />
        )}
        {value && (
          <div className="mt-4 relative rounded-lg overflow-hidden border border-black/10">
            <img src={fileUrl(value)} alt="" className="w-full h-44 object-cover" />
          </div>
        )}
      </FieldShell>
    );
  }

  return (
    <FieldShell label={field.label}>
      <input
        data-testid={`settings-${field.key}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none border-b border-black/20 focus:border-black py-2 font-sans-modern"
      />
    </FieldShell>
  );
}

function FieldShell({ label, hint, children }) {
  return (
    <div>
      <p className="overline text-[#76746F] mb-2">{label}</p>
      {children}
      {hint && <p className="text-xs text-[#76746F] mt-2">{hint}</p>}
    </div>
  );
}

function ModeButton({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs transition-colors ${
        active ? "bg-[#1A1A1A] text-white" : "bg-white border border-black/15 text-[#76746F] hover:text-[#1A1A1A]"
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function BrandAssets() {
  const [copied, setCopied] = useState("");
  const assets = [
    { id: "logo-qd-dark", name: "QD Monogram — Dark", path: "/logo-qd-dark.svg", bg: "#1A1A1A" },
    { id: "logo-qd-light", name: "QD Monogram — Light", path: "/logo-qd-light.svg", bg: "#FDFBF7" },
    { id: "logo-qd-horizontal", name: "Horizontal Lockup", path: "/logo-qd-horizontal.svg", bg: "#FDFBF7" },
    { id: "favicon", name: "Favicon", path: "/favicon.svg", bg: "#1A1A1A" },
  ];
  const copy = async (path) => {
    const full = window.location.origin + path;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(path);
      toast.success("Link copied");
      setTimeout(() => setCopied(""), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#76746F]">
        Download the official QD logo files below. SVG is vector — scales to any size without
        quality loss. Use the dark version on light backgrounds and vice versa.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {assets.map((a) => (
          <div key={a.id} data-testid={`brand-asset-${a.id}`} className="bg-white border border-black/10 rounded-xl overflow-hidden">
            <div className="flex items-center justify-center h-40 p-6" style={{ background: a.bg }}>
              <img src={a.path} alt={a.name} className="max-h-full max-w-full" />
            </div>
            <div className="px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{a.name}</p>
                <p className="text-xs text-[#76746F]">{a.path.split("/").pop()}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => copy(a.path)}
                  className="w-9 h-9 rounded-full border border-black/15 hover:bg-black hover:text-white transition-colors flex items-center justify-center"
                  aria-label="Copy link"
                >
                  {copied === a.path ? <Check size={14} /> : <Copy size={14} />}
                </button>
                <a
                  href={a.path}
                  download
                  className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white px-3.5 py-2 rounded-full hover:bg-[#D4A373] transition-colors text-xs"
                >
                  <Download size={13} /> Download
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#76746F]">
        Tip: SVGs can be exported to PNG at any resolution using any image tool (e.g. open in
        Figma / Illustrator or run <code className="px-1 py-0.5 bg-black/5 rounded">inkscape file.svg --export-png=file.png -w 1024</code>).
      </p>
    </div>
  );
}
